import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  arrayOf,
  bool,
  func,
  node,
  number,
  oneOf,
  shape,
  string,
  object,
  oneOfType,
} from 'prop-types';
import { cx } from '@emotion/css';

import Typography from '@/components/Typography';

import useSelectSearch from './hooks/useSelectSearch';
import InputRadioItem from './InputRadioItem';
import {
  stySelectWrapper,
  stySelectSelector,
  stySelectSelectorInner,
  stySelectIcon,
  stySelectOptionsContainer,
  stySelectOptionItem,
} from './style';

const normalizeValue = (val) => {
  if (val === undefined || val === null || val === '') return null;

  switch (typeof val) {
    case 'string':
      return {
        description: '',
        icon: null,
        text: val,
        value: val,
      };
    case 'object':
    default:
      return val;
  }
};

const getPreviousOption = (current, haystack = []) => {
  const i = haystack?.findIndex((v) => v?.value === current?.value) || -1;
  return i - 1 >= 0 ? haystack[i - 1] : haystack[0];
};

const getNextOption = (current, haystack = []) => {
  const i = haystack?.findIndex((v) => v?.value === current?.value);
  return i + 1 < haystack.length ? haystack[i + 1] : haystack[haystack.length - 1];
};

const InputSelect = (props) => {
  const {
    id,
    name,
    className,
    defaultValue: rawDefaultValue,
    disabled,
    error,
    icon,
    onBlur,
    onChange,
    onClick,
    onSearchChange,
    options: rawOptions,
    maxOptions,
    placeholder,
    placeholderSearch,
    searchByApi,
    style,
    textHint,
    value: rawValue,
    variant,
    withSearch,
  } = props;

  const wrapperRef = useRef(null);
  const mounted = useRef(false);

  const options = useMemo(() => rawOptions.map((opt) => normalizeValue(opt)), [rawOptions]);
  const optionsType = useMemo(() => typeof (rawOptions?.[0] || ''), [rawOptions]);
  const defaultValue = useMemo(() => {
    const normalized = normalizeValue(rawDefaultValue);
    const found = options.find((opt) => opt.value === normalized?.value);
    return found || null;
  }, [rawDefaultValue, options]);
  const propsValue = useMemo(() => normalizeValue(rawValue), [rawValue]);

  // Store value on state when component is uncontrolled
  const isControlled = useMemo(() => propsValue !== null, [propsValue]);
  const [isOpen, setIsOpen] = useState(false);
  const [stateValue, setStateValue] = useState(defaultValue);
  const [displayOptions, setDisplayOptions] = useState(options);
  const usedValue = isControlled ? propsValue : stateValue;

  const { resetSearch, renderSearch } = useSelectSearch({
    id,
    options,
    displayOptions,
    setDisplayOptions,
    props: { placeholderSearch, onSearchChange, searchByApi },
  });

  const resetState = () => {
    setIsOpen(false);
    setDisplayOptions(options);
    resetSearch();
  };

  useEffect(() => {
    setDisplayOptions(options);
  }, [options]);

  useEffect(() => {
    if (defaultValue && !mounted.current) {
      setStateValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        resetState();
        onBlur();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef, options]);

  const handleToggleOpen = () => {
    if (!disabled) {
      if (isOpen) {
        resetState();
        onBlur();
      } else {
        setIsOpen(true);
      }
      onClick();
    }
  };

  const handleSelectOption = (opt) => {
    if (!opt?.disabled) {
      setStateValue(opt);
      setIsOpen(false);
      resetSearch();
      switch (optionsType) {
        case 'object':
          onChange(opt);
          break;
        case 'string':
        default:
          onChange(opt.value);
          break;
      }
    }
  };

  const handleInputKeyDown = (event) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        handleToggleOpen();
        break;
      case 'ArrowUp':
        handleSelectOption(getPreviousOption(usedValue, options));
        break;
      case 'ArrowDown':
        handleSelectOption(getNextOption(usedValue, options));
        break;
      default:
        break;
    }
  };

  const renderValue = (_val) => (
    <>
      {_val.icon && <img src={_val.icon} alt="" />}
      {_val.text}
    </>
  );

  const renderIcon = () =>
    icon || (
      <span className={`${stySelectIcon({ isOpen })}`}>
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 5.5L8 11.5L14 5.5"
            stroke="#ADADAD"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );

  const renderMessage = () => {
    switch (true) {
      case !!error:
        // If error exists, show error as highest priority
        return (
          <div>
            <Typography type="alert" alertType="error" style={{ marginTop: 4 }}>
              {error}
            </Typography>
          </div>
        );
      case !!textHint:
        // If no higher priorities present, display textHint if available
        return (
          <div>
            <Typography type="label" style={{ marginTop: 4 }}>
              {textHint}
            </Typography>
          </div>
        );
      default:
        return null;
    }
  };

  const optionsContainerHeight =
    maxOptions && displayOptions?.length > maxOptions ? maxOptions * 36 : 'auto';

  return (
    <div
      ref={wrapperRef}
      className={`${stySelectWrapper({ disabled })} ${className}`}
      style={style}
    >
      {/* Input */}
      <div
        id={id}
        data-testid={id}
        name={name}
        role="button"
        tabIndex="0"
        onClick={handleToggleOpen}
        onKeyDown={handleInputKeyDown}
        className={stySelectSelector({ disabled, error, unselected: !usedValue })}
      >
        <span className={stySelectSelectorInner({ value: usedValue })} data-testid={`${id}-value`}>
          {usedValue ? renderValue(usedValue) : placeholder}
        </span>
        {!disabled && renderIcon()}
      </div>

      {/* Options */}
      {!disabled && (
        <div
          className={`${stySelectOptionsContainer({ isOpen, optionsContainerHeight })}`}
          data-testid={`${id}-options`}
        >
          {withSearch && renderSearch()}
          <div>
            {displayOptions.map((opt, idx) => (
              <div
                key={opt.value ?? idx}
                role="button"
                tabIndex="0"
                className={cx(stySelectOptionItem({ variant, disabled: opt.disabled }), {
                  selected: opt.value === usedValue?.value,
                })}
                onClick={() => handleSelectOption(opt)}
                onKeyDown={(e) => e.key === 'Enter' && handleSelectOption(opt)}
                data-testid={`${id}-opt-${idx}`}
              >
                {variant === 'default' && renderValue(opt)}
                {variant === 'radio' && (
                  <>
                    <InputRadioItem
                      id={`${id}-radio-${idx}`}
                      name={`${name}-radio-${idx}`}
                      value={opt.text}
                      checked={opt.value === usedValue?.value}
                      disabled={opt.disabled}
                      textColor="inherit"
                    >
                      {opt.text}
                    </InputRadioItem>
                    {opt.description && (
                      <Typography type="label" style={{ marginTop: 4 }}>
                        {opt.description}
                      </Typography>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {renderMessage()}
    </div>
  );
};

const optionPropType = oneOfType([
  string,
  shape({
    description: string,
    disabled: bool,
    icon: string,
    text: string,
    value: oneOfType([string, number]),
  }),
]);

InputSelect.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  className: string,
  defaultValue: optionPropType,
  disabled: bool,
  error: string,
  icon: node,
  maxOptions: number,
  onBlur: func,
  onChange: func,
  onClick: func,
  onSearchChange: func,
  options: arrayOf(optionPropType),
  placeholder: string,
  placeholderSearch: string,
  searchByApi: bool,
  style: object,
  textHint: string,
  value: optionPropType,
  variant: oneOf(['default', 'radio']),
  withSearch: bool,
};

InputSelect.defaultProps = {
  className: '',
  icon: null,
  defaultValue: null,
  disabled: false,
  error: '',
  onBlur: () => {},
  onChange: () => {},
  onClick: () => {},
  onSearchChange: () => {},
  options: [],
  maxOptions: null,
  placeholder: 'Pilih...',
  placeholderSearch: 'Cari...',
  searchByApi: false,
  style: {},
  textHint: '',
  value: null,
  variant: 'default',
  withSearch: false,
};

export default InputSelect;
